import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import HttpService from "../../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import { useState } from "react";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AddEmployer from "../../components/AddEmployer";
import MarketingMaterials from "./MarketingMaterials";
import CreateUser from "../../components/CreateUser";
import UsersManagementGrid from "../../components/UsersManagementGrid";
import TuneIcon from "@mui/icons-material/Tune";
import EmployerAttributesConfig from "../EmployerAttributesConfig";

async function fetchEmployer() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/all`
  );
  return response;
}

export default function EmployerConfig() {
  const [marketingDlg, setMarketingDlg] = useState();
  const [createUserDlg, setCreateUserDlg] = useState(null);
  const [employerAttributesDialog, setEmployerAttributesDialog] = useState({
    open: false,
    employer: null,
  });
  const [viewUsersDialog, setViewUsersDialog] = useState({
    open: false,
    employerId: null,
  });
  const [previousValues, setPreviousValues] = useState({});
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // eslint-disable-next-line no-unused-vars
  const updateEmployeeAttributeMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().put(
        window.API_URL + `/employer/attributes/${values.id}`,
        values.row
      );
    },
    {
      onSuccess: (data, value) => {
        setPreviousValues((prevPreviousValues) => {
          const newPreviousValues = { ...prevPreviousValues };
          delete newPreviousValues[value.id];
          return newPreviousValues;
        });
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error updating the attributes",
        });
      },
    }
  );

  // eslint-disable-next-line no-unused-vars
  const deleteEmployeeAttributeMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/employer/attributes/${values.id}`
      );
    },
    {
      onSuccess: (dataResponse, value) => {
        data.data = data.data.filter((row) => row.id !== value.id);
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: "Error deleting the attribute",
        });
      },
    }
  );

  const handleEditCellChange = (params, id, field, oldValue, newValue) => {
    const encryptedProperty =
      params.row.properties !== null &&
      Array.isArray(params.row.properties) &&
      params.row.properties.some(
        (property) => property.property === "ENCRYPTED"
      );
    if (!encryptedProperty) {
      if (newValue !== undefined && oldValue !== newValue) {
        setPreviousValues((prevPreviousValues) => ({
          ...prevPreviousValues,
          [id]: { ...prevPreviousValues[id], [field]: oldValue },
        }));
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const clearEditedRow = (params) => {
    params.row.value = previousValues[params.id].value;
    setPreviousValues((prevPreviousValues) => {
      const newPreviousValues = { ...prevPreviousValues };
      delete newPreviousValues[params.id];
      return newPreviousValues;
    });
  };

  const isEncrypted = (params) => {
    return (
      params.row.properties !== null &&
      Array.isArray(params.row.properties) &&
      params.row.properties.some(
        (property) => property.property === "ENCRYPTED"
      )
    );
  };

  const onViewUsersClick = (employer) => {
    setViewUsersDialog({
      open: true,
      employerId: employer.idEmployer,
    });
  };

  const handleCloseViewUsersDialog = () => {
    setViewUsersDialog({
      open: false,
      employerId: null,
    });
  };

  const onViewEmployerAttributesClick = (employer) => {
    setEmployerAttributesDialog({
      open: true,
      employer,
    });
  };

  const handleCloseEmployerAttributesDialog = () => {
    setEmployerAttributesDialog({
      open: false,
      employer: null,
    });
  };

  const columns = [
    { field: "idEmployer", headerName: "id", width: 300 },
    { field: "name", headerName: "Employer", width: 350, hide: false },
    { field: "description", headerName: "Description", width: 350 },
    {
      field: "actions",
      headerName: "Actions",
      width: 300,
      renderCell: (params) => (
        <Box display="flex" gap={0}>
          <Tooltip title="Marketing Materials">
            <Button
              size="small"
              onClick={() => {
                setMarketingDlg(params.row);
              }}
            >
              <QrCodeIcon />
            </Button>
          </Tooltip>
          <Tooltip title="Create User for this Company">
            <Button
              size="small"
              onClick={() => {
                setCreateUserDlg(params.row);
              }}
            >
              <PersonAddIcon />
            </Button>
          </Tooltip>
          <Tooltip title="View Users in this Company">
            <Button
              size="small"
              onClick={() => {
                onViewUsersClick(params.row);
              }}
            >
              <PeopleIcon />
            </Button>
          </Tooltip>
          <Tooltip title="View Company Attributes">
            <Button
              size="small"
              onClick={() => {
                onViewEmployerAttributesClick(params.row);
              }}
            >
              <TuneIcon variant="contained" />
            </Button>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "lastChangedDate",
      headerName: "Last changed",
      width: 150,
      hide: true,
    },
    { field: "changedBy", headerName: "Changed by", width: 120, hide: true },
  ];

  const { data, isLoading } = useQuery(["employer"], fetchEmployer);
  const [addEmployerDlg, setAddEmployerDlg] = useState(false);

  const addEmployerlgFunct = (event) => {
    return (
      <Dialog open={addEmployerDlg} fullWidth onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Add Company
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddEmployerDlg(false);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <AddEmployer handleClose={() => setAddEmployerDlg(false)} />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Dialog
        open={!!marketingDlg}
        onClose={() => setMarketingDlg(undefined)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <MarketingMaterials employer={marketingDlg} />
        </DialogContent>
        <Box
          sx={{
            padding: 2,
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button onClick={() => setMarketingDlg()}>Close</Button>
        </Box>
      </Dialog>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12}>
            <Box display="flex">
              <Typography variant="h5">Companies </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{ marginLeft: "1rem" }}
                onClick={() => setAddEmployerDlg(true)}
              >
                Add Company
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "80vh" }}>
            <DataGrid
              key={data.data.idEmployer}
              rows={data.data}
              columns={columns}
              getRowHeight={() => 35}
              density="compact"
              onCellEditStop={(params, event) =>
                handleEditCellChange(
                  params,
                  params.id,
                  params.field,
                  params.value,
                  event?.target?.value
                )
              }
              disableDensitySelector
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              isCellEditable={(params) => !isEncrypted(params)}
              getRowId={(row) => row.idEmployer}
            />
          </Grid>
        </Grid>
      )}
      {addEmployerDlg ? addEmployerlgFunct() : ""}

      {/* Create User Dialog */}
      {createUserDlg && (
        <CreateUser
          open={!!createUserDlg}
          setOpen={(isOpen) => {
            if (!isOpen) setCreateUserDlg(null);
          }}
          employerId={createUserDlg.idEmployer}
        />
      )}

      {/* View Users Dialog */}
      {viewUsersDialog.open && (
        <Dialog
          open={viewUsersDialog.open}
          fullWidth
          maxWidth="lg"
          onClose={handleCloseViewUsersDialog}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleIcon sx={{ color: "white" }} />
              <Typography variant="h6" sx={{ color: "white" }}>
                Users in Company
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseViewUsersDialog}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ paddingTop: 3, minHeight: 500 }}>
            <UsersManagementGrid
              employerId={viewUsersDialog.employerId}
              showAddButton={false}
              showSubDepartmentFilter={true}
              height="400px"
            />
          </DialogContent>
          <Box
            sx={{
              padding: 2,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button onClick={handleCloseViewUsersDialog}>Close</Button>
          </Box>
        </Dialog>
      )}

      {employerAttributesDialog.open && (
        <Dialog
          open={employerAttributesDialog.open}
          fullWidth
          maxWidth="xl"
          onClose={handleCloseEmployerAttributesDialog}
        >
          <Box
            sx={{
              backgroundColor: "primary.main",
              px: 3,
              minHeight: 52,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneIcon sx={{ color: "white" }} />
              <Typography variant="h6" sx={{ color: "white" }}>
                Company Attributes - {employerAttributesDialog.employer?.name}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseEmployerAttributesDialog}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ paddingTop: 3, minHeight: 600 }}>
            <EmployerAttributesConfig
              employerId={employerAttributesDialog.employer?.idEmployer}
              employerName={employerAttributesDialog.employer?.name}
            />
          </DialogContent>
          <Box
            sx={{
              padding: 2,
              display: "flex",
              justifyContent: "flex-end",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button onClick={handleCloseEmployerAttributesDialog}>Close</Button>
          </Box>
        </Dialog>
      )}

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
